/**
 * InitiativeDetailContent — Shared read-only layout for initiative details.
 *
 * Used by both the settings initiative detail page (view mode only)
 * and the recommendations preview pane. All display-only cards live here
 * to ensure a single source of truth for rendering.
 */

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
    ArrowRight,
    Clock,
    Users,
    TrendingUp,
    Zap,
    DollarSign,
    Shield,
    BarChart3,
    Wrench,
    Target,
    CheckCircle,
    AlertTriangle,
    Layers,
} from 'lucide-react'

// ── Color maps ────────────────────────────────────────────────────────
export const PRIORITY_COLORS: Record<string, string> = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200',
}

export const RISK_COLORS: Record<string, string> = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-green-100 text-green-700',
}

// ── Tag Section (read-only) ───────────────────────────────────────────
function ReadOnlyTagSection({ title, items, icon: Icon }: { title: string; items: string[]; icon: React.ElementType }) {
    if (!items || items.length === 0) return null
    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold">{title}</h3>
                    <span className="text-xs text-muted-foreground">({items.length})</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {items.map((item, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{item}</Badge>
                    ))}
                </div>
            </CardContent>
        </Card>
    )
}

// ── Badge Row ─────────────────────────────────────────────────────────
export function InitiativeBadgeRow({ data }: { data: any }) {
    const domainLabel = data.domain || data.category || ''
    const riskLabel = data.risk_level || ''
    const fromLevel = data.from_maturity_level ?? data.from_level
    const toLevel = data.to_maturity_level ?? data.to_level
    const hasLevelTransition = fromLevel != null && toLevel != null

    return (
        <div className="flex flex-wrap gap-2">
            {domainLabel && (
                <Badge variant="outline" className="capitalize">{domainLabel}</Badge>
            )}
            <Badge className={`capitalize border ${PRIORITY_COLORS[data.priority] || ''}`}>
                {data.priority} priority
            </Badge>
            {hasLevelTransition && (
                <Badge variant="secondary">
                    Level {fromLevel} <ArrowRight className="h-3 w-3 mx-1 inline" /> Level {toLevel}
                </Badge>
            )}
            {riskLabel && (
                <Badge className={`capitalize ${RISK_COLORS[riskLabel] || ''}`}>
                    {riskLabel} risk
                </Badge>
            )}
        </div>
    )
}

// ── Main Content Body ─────────────────────────────────────────────────
export function InitiativeDetailContent({ data }: { data: any }) {
    const hasBudget = data.budget_range_min && data.budget_range_max
    const teamText = data.team_size_min && data.team_size_max
        ? `${data.team_size_min}–${data.team_size_max}`
        : data.team_size
            ? `${data.team_size}`
            : '—'
    const impactLabel = data.impact_level || data.impact || ''
    const effortLabel = data.effort_level || data.effort || ''

    return (
        <div className="space-y-5">
            {/* Description */}
            {data.description && (
                <Card>
                    <CardContent className="p-4">
                        <h3 className="text-sm font-semibold mb-2">Description</h3>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{data.description}</p>
                    </CardContent>
                </Card>
            )}

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                    <CardContent className="p-3 text-center">
                        <Clock className="h-4 w-4 mx-auto mb-1 text-blue-500" />
                        <div className="text-lg font-bold">{data.duration_weeks ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">weeks</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-3 text-center">
                        <Users className="h-4 w-4 mx-auto mb-1 text-purple-500" />
                        <div className="text-lg font-bold">{teamText}</div>
                        <div className="text-xs text-muted-foreground">team members</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-3 text-center">
                        <TrendingUp className="h-4 w-4 mx-auto mb-1 text-green-500" />
                        <div className="text-lg font-bold capitalize">{impactLabel || '—'}</div>
                        <div className="text-xs text-muted-foreground">impact</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-3 text-center">
                        <Zap className="h-4 w-4 mx-auto mb-1 text-orange-500" />
                        <div className="text-lg font-bold capitalize">{effortLabel || '—'}</div>
                        <div className="text-xs text-muted-foreground">effort</div>
                    </CardContent>
                </Card>
            </div>

            {/* Budget / Business Value / ROI */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <h3 className="text-sm font-semibold">Budget Range</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {hasBudget
                                ? `$${data.budget_range_min}k – $${data.budget_range_max}k`
                                : data.budget_range || 'Not specified'}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <BarChart3 className="h-4 w-4 text-muted-foreground" />
                            <h3 className="text-sm font-semibold">Business Value</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{data.business_value || 'Not specified'}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <TrendingUp className="h-4 w-4 text-muted-foreground" />
                            <h3 className="text-sm font-semibold">ROI Estimate</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{data.roi_estimate || 'Not specified'}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Tag Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ReadOnlyTagSection title="Required Technologies" items={data.required_technologies || data.technologies || []} icon={Wrench} />
                <ReadOnlyTagSection title="Required Skills" items={data.required_skills || []} icon={Target} />
                <ReadOnlyTagSection title="Recommended Tools" items={data.recommended_tools || []} icon={Wrench} />
                <ReadOnlyTagSection title="Success Metrics" items={data.success_metrics || []} icon={CheckCircle} />
                <ReadOnlyTagSection title="KPIs" items={data.kpis || []} icon={BarChart3} />
                <ReadOnlyTagSection title="Prerequisites" items={data.prerequisites || []} icon={Layers} />
                <ReadOnlyTagSection title="Mitigation Strategies" items={data.mitigation_strategies || []} icon={Shield} />
                <ReadOnlyTagSection title="Risk Factors" items={data.risk_factors || []} icon={AlertTriangle} />
            </div>

            {/* Dependencies */}
            {data.dependencies && data.dependencies.length > 0 && (
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Layers className="h-4 w-4 text-muted-foreground" />
                            <h3 className="text-sm font-semibold">Dependencies</h3>
                            <span className="text-xs text-muted-foreground">({data.dependencies.length})</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {data.dependencies.map((dep: any) => (
                                <Badge key={dep.id} variant="secondary" className="text-xs">{dep.title}</Badge>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Phases */}
            {((data.phases && data.phases.length > 0) || (data.implementation_timeline?.phases && data.implementation_timeline.phases.length > 0)) && (
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Layers className="h-4 w-4 text-muted-foreground" />
                            <h3 className="text-sm font-semibold">Phases</h3>
                            <span className="text-xs text-muted-foreground">
                                ({(data.phases || data.implementation_timeline?.phases || []).length})
                            </span>
                        </div>
                        <div className="space-y-3">
                            {(data.phases || data.implementation_timeline?.phases || []).map((phase: any, idx: number) => (
                                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-semibold shrink-0">
                                            {idx + 1}
                                        </span>
                                        <div className="font-medium text-sm">
                                            {phase.name}
                                        </div>
                                    </div>
                                    {phase.description && (
                                        <p className="text-xs text-muted-foreground mt-1 ml-8">{phase.description}</p>
                                    )}
                                    {(phase.duration_weeks || phase.duration) && (
                                        <p className="text-xs text-muted-foreground mt-1 ml-8">
                                            Duration: {phase.duration_weeks || phase.duration} weeks
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    )
}
